/**
 * @description Invoice detail view — header dates, status, send/pay actions.
 * @dependencies invoiceHelpers.formatTimestampDate, formatInvoiceDate, isInvoiceOverdue;
 * invoiceService; settingsService; CompanyContext
 * @notes Created uses created_at (timestamptz) via formatTimestampDate. Due uses
 * the DATE column via formatInvoiceDate. Overdue uses company primary-location TZ.
 * Unsent portal guidance uses sentCount plus the existing portal visibility setting.
 * Invoice history scrolls in-box; refund amounts prefer refund_amount_cents.
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import invoiceService from '../../services/api/invoiceService';
import { refundsService } from '../../services/api';
import {
  formatInvoiceDate,
  formatTimestampDate,
  formatCurrency,
  isInvoiceOverdue,
  namedDiscountLines
} from '../../utils/invoiceHelpers';
import { formatPaymentMethodLabel } from '../../utils/paymentMethodDisplay';
import RefundModal from '../shared/RefundModal';
import ApplyCreditModal from '../shared/ApplyCreditModal';
import StatusBadge from '../shared/StatusBadge';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';
import creditMemoService from '../../services/api/creditMemoService';
import CreditMemoDetailsModal from '../creditMemos/CreditMemoDetailsModal';
import SendInvoiceModal from './SendInvoiceModal';
import SendReceiptModal from './SendReceiptModal';
import PdfPreviewModal from './PdfPreviewModal';
import { useCompany } from '../../contexts/CompanyContext';
import settingsService from '../../services/api/settingsService';

function formatRefundHistoryAmount(details) {
  if (details?.refund_amount_cents != null && details.refund_amount_cents !== '') {
    return formatCurrency(Number(details.refund_amount_cents) / 100);
  }
  if (details?.refund_amount != null && details.refund_amount !== '') {
    return formatCurrency(Number(details.refund_amount));
  }
  return formatCurrency(0);
}

function ScrollableInvoiceHistory({ children }) {
  const scrollRef = useRef(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    const updateCue = () => {
      setCanScrollMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
    };

    updateCue();
    el.addEventListener('scroll', updateCue);
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateCue)
      : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateCue);
      observer?.disconnect();
    };
  }, [children]);

  return (
    <div className="relative">
      <div ref={scrollRef} className="max-h-64 overflow-y-auto pr-0.5">
        {children}
      </div>
      {canScrollMore && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-7 bg-gradient-to-t from-gray-50 to-transparent flex items-end justify-center"
          aria-hidden="true"
        >
          <svg className="w-3.5 h-3.5 text-gray-400 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      )}
    </div>
  );
}

function InvoiceDetails() {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const { company } = useCompany();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [expandedHistoryGroups, setExpandedHistoryGroups] = useState({});
  
  // Refund state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundError, setRefundError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Credit memo state
  const [showApplyCreditModal, setShowApplyCreditModal] = useState(false);
  const [customerCredit, setCustomerCredit] = useState(null);

  // Credit memo details modal state (for viewing applied credits in history)
  const [showCreditMemoDetailsModal, setShowCreditMemoDetailsModal] = useState(false);
  const [selectedCreditMemoId, setSelectedCreditMemoId] = useState(null);

  // Send invoice/reminder modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendMode, setSendMode] = useState('invoice');

  // Send receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // PDF preview modal state
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Existing portal setting; null avoids showing inaccurate guidance if loading fails.
  const [displayUnsentInPortal, setDisplayUnsentInPortal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setDisplayUnsentInPortal(null);

    settingsService.getSetting('invoicing.display_unsent_in_portal')
      .then(setting => {
        if (!cancelled) {
          setDisplayUnsentInPortal(setting?.value === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDisplayUnsentInPortal(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [company?.id]);

  useEffect(() => {
    // Early return if no invoice number or undefined string - this prevents the race condition
    if (!invoiceNumber || invoiceNumber === 'undefined') {
      if (invoiceNumber === 'undefined') {
        setError('Invalid invoice number. Please navigate from the invoice list.');
        setLoading(false);
      }
      return;
    }
  
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await invoiceService.getInvoice(invoiceNumber);
        console.log('Invoice data:', data);
        const invoice = data.success ? data.invoice : null;
        const items = data.success ? data.items : [];
        if (invoice) {
          // Phase 5.2: Use backend-provided dollar values directly (no manual conversions)
          invoice.subtotal = parseFloat(invoice.subtotal) || 0;
          invoice.taxableSubtotal = parseFloat(invoice.taxableSubtotal) || 0;
          invoice.taxAmount = parseFloat(invoice.taxAmount) || 0;
          invoice.discountAmount = parseFloat(invoice.discountAmount) || 0;
          invoice.shippingCost = parseFloat(invoice.shippingCost) || 0;
          invoice.totalAmount = parseFloat(invoice.totalAmount) || 0;
          invoice.amountPaid = parseFloat(invoice.amountPaid) || 0;
          invoice.balanceDue = (invoice.totalAmount - invoice.amountPaid);
          invoice.balancePaid = invoice.amountPaid;
          // Clean architecture: Items are separate in response, attach to invoice for compatibility
          invoice.items = (items || []).map(item => ({
            ...item,
            unitPrice: parseFloat(item.unitPrice) || 0,
            total: parseFloat(item.total) || 0
          }));
          setInvoice(invoice);
          setInternalNotes(invoice.internalNotes || '');
          
          // Load invoice history on initial page load
          try {
            const historyResponse = await invoiceService.getInvoiceHistory(invoiceNumber);
            if (historyResponse.success && historyResponse.history) {
              setInvoice(prevInvoice => ({
                ...prevInvoice,
                history: historyResponse.history
              }));
            }
          } catch (historyError) {
            console.error('Failed to load invoice history:', historyError);
            // Don't fail the entire page load if history fails
          }
        } else {
          setError('Invoice data not found');
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvoice();
  }, [invoiceNumber]);

  // Load customer credit when invoice is available
  useEffect(() => {
    const loadCustomerCredit = async () => {
      if (!invoice?.customerPublicId) return;
      try {
        const result = await creditMemoService.getCustomerCredit(`cust-${invoice.customerPublicId}`);
        if (result.success) {
          setCustomerCredit(result);
        }
      } catch (err) {
        console.error('Error loading customer credit:', err);
      }
    };
    loadCustomerCredit();
  }, [invoice?.customerPublicId]);

  // Function to refresh invoice history without changing UI
  const refreshInvoiceHistory = async () => {
    if (!invoiceNumber || invoiceNumber === 'undefined' || !invoice) {
      return;
    }

    try {
      const historyResponse = await invoiceService.getInvoiceHistory(invoiceNumber);
      
      if (historyResponse.success && historyResponse.history) {
        // Update invoice with fresh history data, preserving all other invoice data
        setInvoice(prevInvoice => ({
          ...prevInvoice,
          history: historyResponse.history
        }));
      }
    } catch (error) {
      console.error('Failed to refresh invoice history:', error);
      // Silently fail to avoid disrupting UX - history will use last fetched data
    } finally {
      // History refresh completed
    }
  };

  const groupHistoryEvents = (history) => {
    if (!history || history.length === 0) return [];
    
    const groups = [];
    let currentGroup = {
      action: history[0].action,
      events: [history[0]],
      firstDate: history[0].actionDate
    };
    
    for (let i = 1; i < history.length; i++) {
      if (history[i].action === currentGroup.action) {
        currentGroup.events.push(history[i]);
      } else {
        groups.push(currentGroup);
        currentGroup = {
          action: history[i].action,
          events: [history[i]],
          firstDate: history[i].actionDate
        };
      }
    }
    groups.push(currentGroup);
    
    return groups;
  };

  const toggleHistoryGroup = (index) => {
    setExpandedHistoryGroups(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await invoiceService.updateInvoice(invoice.invoiceNumber, { internalNotes: internalNotes });
      setEditingNotes(false);
      setInvoice(prev => ({ ...prev, internalNotes: internalNotes }));
      
      // Refresh history to show the update event
      await refreshInvoiceHistory();
    } catch (err) {
      console.error('Error saving notes:', err);
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSendInvoiceSuccess = async (result) => {
    // Status stays 'open' — sending is a delivery event, not a status change
    // Update sent tracking fields from the response
    setInvoice(prev => ({
      ...prev,
      sentCount: (prev.sentCount || 0) + 1,
      lastSentDate: new Date().toISOString()
    }));
    setSuccessMessage(result?.message || 'Invoice sent successfully');
    setTimeout(() => setSuccessMessage(''), 5000);
    await refreshInvoiceHistory();
  };

  // Check if invoice has refundable credit card transactions
  const hasRefundableTransactions = () => {
    if (!invoice || !invoice.payments) {
      return false;
    }
    
    console.log('=== MULTI-PAYMENT INVOICE DEBUG ===');
    console.log('Invoice:', invoice.invoiceNumber);
    console.log('Total payments:', invoice.payments.length);
    
    invoice.payments.forEach((payment, index) => {
      console.log(`Payment ${index + 1}:`, {
        id: payment.id,
        amount: payment.amount,
        paymentMethod: payment.paymentMethod,
        cardType: payment.cardType,
        status: payment.status,
        channel: payment.channel,
        refundedAmount: payment.refundedAmount
      });
    });
    
    const refundablePayments = invoice.payments.filter(payment => {
      // Check if it's a credit card payment that's approved and not already refunded
      const hasCardType = payment.cardType && 
                         ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'].includes(payment.cardType.toUpperCase());
      
      // For invoice payments, if they exist and are tied to a paid invoice, they're considered "approved"
      // Unlike direct transactions, invoice payments don't have a separate approval status
      // Let's be more inclusive about payment types since multi-payment invoices might have different data structures
      const isApproved = (payment.paymentMethod === 'credit_card' || hasCardType) && 
                        (payment.status === 'APPROVAL' || payment.status === undefined || payment.status === 'paid');
      
      const hasRefundableBalance = (payment.amount || 0) > (payment.refundedAmount || 0);
      const isNotExternal = payment.channel !== 'external';
      
      const isRefundable = hasCardType && isApproved && hasRefundableBalance && isNotExternal;
      
      console.log(`Payment ${payment.id} refundable:`, isRefundable, {
        hasCardType,
        isApproved,
        hasRefundableBalance,
        isNotExternal,
        paymentMethod: payment.paymentMethod,
        cardType: payment.cardType,
        status: payment.status,
        amount: payment.amount,
        refundedAmount: payment.refundedAmount,
        channel: payment.channel
      });
      
      return isRefundable;
    });
    
    console.log('Refundable payments found:', refundablePayments.length);
    console.log('=== END DEBUG ===');
    
    return refundablePayments.length > 0;
  };

  // Get refundable transactions for modal display
  const getRefundableTransactions = () => {
    if (!invoice || !invoice.payments) return [];
    
    return invoice.payments.filter(payment => {
      const hasCardType = payment.cardType && 
                         ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'].includes(payment.cardType.toUpperCase());
      
      // For invoice payments, if they exist and are tied to a paid invoice, they're considered "approved"
      // Let's be more inclusive about payment types since multi-payment invoices might have different data structures
      const isApproved = (payment.paymentMethod === 'credit_card' || hasCardType) && 
                        (payment.status === 'APPROVAL' || payment.status === undefined || payment.status === 'paid');
      
      const hasRefundableBalance = (payment.amount || 0) > (payment.refundedAmount || 0);
      const isNotExternal = payment.channel !== 'external';
      
      return hasCardType && isApproved && hasRefundableBalance && isNotExternal;
    });
  };

  // Handle refund button click
  const handleRefundClick = (payment = null) => {
    const refundableTransactions = getRefundableTransactions();
    
    if (refundableTransactions.length === 1) {
      // Single payment - go directly to refund modal
      setSelectedTransaction(refundableTransactions[0]);
      setShowRefundModal(true);
    } else if (payment) {
      // Specific payment selected
      setSelectedTransaction(payment);
      setShowRefundModal(true);
    } else {
      // Multiple payments - should show selection modal (future enhancement)
      alert('Multiple payment refund selection coming soon');
    }
  };

  // Handle refund confirmation
  const handleRefundConfirm = async (refundData) => {
    try {
      setProcessingRefund(true);
      setRefundError('');
      
      await refundsService.createRefund({ // result unused - API call for side effects
        transactionId: selectedTransaction.transactionReference,
        amount: refundData.amount,
        reason: refundData.reason,
        notes: refundData.notes
      });
      
      setSuccessMessage(`Refund of ${formatCurrency(refundData.amount)} processed successfully`);
      setShowRefundModal(false);
      setSelectedTransaction(null);
      
      // Refresh invoice data
      if (invoiceNumber && invoiceNumber !== 'undefined') {
        const updatedInvoice = await invoiceService.getInvoice(invoiceNumber);
        setInvoice(updatedInvoice);
        
        // Refresh history to show the refund event
        await refreshInvoiceHistory();
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Refund failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'An error occurred while processing the refund';
      setRefundError(errorMessage);
    } finally {
      setProcessingRefund(false);
    }
  };

  // Status logic moved to centralized StatusBadge component

  // Show nothing while params are loading
if (!invoiceNumber) {
  return null;
}

if (loading) {
  return (
    <div className="flex justify-center items-center py-10">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
    </div>
  );
}

  if (error || !invoice) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Invoice not found'}
        </div>
      </div>
    );
  }

  // Use centralized StatusBadge component
  const isPaid = invoice.paymentStatus === 'paid';
  const isVoided = invoice.paymentStatus === 'voided';
  const hasBeenSent = (invoice.sentCount || 0) > 0;
  const isOverdue = typeof invoice.isOverdue === 'boolean'
    ? invoice.isOverdue
    : isInvoiceOverdue(invoice.dueDate, invoice.paymentStatus, company?.timezone);
  const historyGroups = groupHistoryEvents(invoice.history);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Invoices
        </button>
        
        <div className="flex items-center gap-4">
          {/* Copy Link Button */}
          <button
            type="button"
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded transition"
            onClick={async () => {
              try {
                const result = await invoiceService.generatePortalLink(invoice.id, invoice.customerId);
                navigator.clipboard.writeText(result.portalUrl);
                alert('Customer portal link copied to clipboard!');
              } catch (error) {
                alert('Failed to generate portal link');
              }
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy Portal Link
          </button>
          
          {/* Status Badge */}
          <StatusBadge 
            type="invoice_status" 
            value={{
              paymentStatus: invoice.paymentStatus,
              status: invoice.status,
              dueDate: invoice.dueDate,
              isOverdue: invoice.isOverdue,
              timeZone: company?.timezone
            }}
            size="sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Billing Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-5">Billing Details</h3>
            
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {invoice.billToCompany
                      || invoice.billToName
                      || '—'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Bill To</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  {invoice.billToEmail ? (
                    <p className="text-sm font-medium text-gray-900 break-all">{invoice.billToEmail}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No email on this invoice</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Email Address</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  {(invoice.billToAddress || invoice.billToAddressLine1 || invoice.billToAddressLine2
                    || invoice.billToCity || invoice.billToState || invoice.billToPostalCode || invoice.billToZip
                    || invoice.billToCountry) ? (
                    <p className="text-sm font-medium text-gray-900 break-words leading-relaxed">
                      {(invoice.billToAddressLine1 || invoice.billToAddress) && (
                        <>{invoice.billToAddressLine1 || invoice.billToAddress}<br /></>
                      )}
                      {invoice.billToAddressLine2 && <>{invoice.billToAddressLine2}<br /></>}
                      {invoice.billToCity}{invoice.billToCity && invoice.billToState ? ', ' : ''}{invoice.billToState}{' '}
                      {invoice.billToPostalCode || invoice.billToZip}
                      {invoice.billToCountry && (
                        <><br />{invoice.billToCountry}</>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No billing address on this invoice</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Billing Address</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowPdfPreview(true)}
              className="w-full mt-8 py-3 bg-brand text-white rounded-xl font-semibold hover:bg-brand-hover transition-all shadow-sm active:scale-[0.98]"
            >
              View Invoice PDF
            </button>
          </div>

          {/* Payment History Card - Desktop Only */}
          <div className="hidden lg:block bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-lg mb-4">Invoice History</h3>

            <ScrollableInvoiceHistory>
              <div className="space-y-3">
                {historyGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="bg-white rounded-lg p-3">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => group.events.length > 1 && toggleHistoryGroup(groupIndex)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                          {getHistoryIcon(group.action)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                          {formatHistoryAction(group.action)}
                          {(group.action === 'payment_recorded' || group.action === 'refund_issued' || group.action === 'payment_voided') && group.events.length === 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (group.events[0]?.transactionId) {
                                  navigate(`/merchant/transactions/${group.events[0].transactionId}`);
                                } else {
                                  console.warn('No transaction ID available for this transaction');
                                }
                              }}
                              className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                            >
                              View
                            </button>
                          )}
                          {group.action === 'credit_applied' && group.events.length === 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const creditMemoId = group.events[0]?.actionDetails?.credit_memo_id;
                                if (creditMemoId) {
                                  setSelectedCreditMemoId(creditMemoId);
                                  setShowCreditMemoDetailsModal(true);
                                } else {
                                  console.warn('No credit memo ID available for this event');
                                }
                              }}
                              className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                            >
                              View
                            </button>
                          )}
                          </p>
                          {group.action === 'credit_applied' && group.events.length === 1 && (
                            <p className="text-sm text-green-600">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.credit_amount || 0) / 100)} via Credit Memo
                              {group.events[0].actionDetails?.credit_memo_number && ` #${group.events[0].actionDetails.credit_memo_number}`}
                            </p>
                          )}
                          {group.action === 'payment_recorded' && group.events.length === 1 && (
                            <p className="text-sm text-gray-500">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.amount || 0) / 100)} via {
                                formatPaymentMethodLabel(
                                  group.events[0].actionDetails?.payment_method
                                    || group.events[0].actionDetails?.paymentMethod
                                )
                              }
                            </p>
                          )}
                          {group.action === 'refund_issued' && group.events.length === 1 && (
                            <p className="text-sm text-gray-500">
                              {formatRefundHistoryAmount(group.events[0].actionDetails)} back to {
                                (() => {
                                  const method = group.events[0].actionDetails?.payment_method || group.events[0].actionDetails?.paymentMethod;
                                  if (method === 'credit_card' || method === 'card') return 'Card';
                                  if (method === 'bank' || method === 'bank_transfer' || method === 'wire' || method === 'ach') return 'Bank';
                                  if (method === 'external' || method === 'other') return 'Other';
                                  if (method === 'check') return 'Check';
                                  if (method === 'cash') return 'Cash';
                                  return method || 'Card';
                                })()
                              }
                            </p>
                          )}
                          {group.action === 'payment_voided' && group.events.length === 1 && (
                            <p className="text-sm text-red-500">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.void_amount || 0) / 100)} voided
                              {group.events[0].actionDetails?.reason && ` - ${group.events[0].actionDetails.reason}`}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(group.firstDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}, {new Date(group.firstDate).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </p>
                        </div>
                      </div>
                      {group.events.length > 1 && (
                        <svg 
                          className={`w-4 h-4 text-gray-400 transition-transform ${expandedHistoryGroups[groupIndex] ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                    
                    {expandedHistoryGroups[groupIndex] && group.events.length > 1 && (
                      <div className="mt-3 pl-11 border-l-2 border-gray-200 ml-5 space-y-4">
                        {group.events.map((event, eventIndex) => (
                          <div key={eventIndex} className="pl-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-400">
                                {group.action === 'payment_recorded' ? 'Received' : 
                                 group.action === 'refund_issued' ? 'Refunded' : 
                                 formatHistoryAction(group.action)}
                                {event.transactionId && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/merchant/transactions/${event.transactionId}`);
                                    }}
                                    className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                                  >
                                    View
                                  </button>
                                )}
                              </p>
                              {group.action === 'payment_recorded' && (
                                <p className="text-sm text-gray-500">
                                  {formatCurrency((event.actionDetails?.amount_cents || event.actionDetails?.amount || 0) / 100)} via {
                                    formatPaymentMethodLabel(
                                      event.actionDetails?.payment_method
                                        || event.actionDetails?.paymentMethod
                                    )
                                  }
                                </p>
                              )}
                              {group.action === 'refund_issued' && (
                                <p className="text-sm text-gray-500">
                                  {formatRefundHistoryAmount(event.actionDetails)} back to {
                                    (() => {
                                      const method = event.actionDetails?.payment_method || event.actionDetails?.paymentMethod;
                                      if (method === 'credit_card' || method === 'card') return 'Card';
                                      if (method === 'bank' || method === 'bank_transfer' || method === 'wire' || method === 'ach') return 'Bank';
                                      if (method === 'external' || method === 'other') return 'Other';
                                      if (method === 'check') return 'Check';
                                      if (method === 'cash') return 'Cash';
                                      return method || 'Card';
                                    })()
                                  }
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {new Date(event.actionDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}, {new Date(event.actionDate).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {(!invoice.history || invoice.history.length === 0) && (
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Invoice Created</p>
                        <p className="text-xs text-gray-500">
                          {new Date(invoice.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}, {new Date(invoice.createdAt).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollableInvoiceHistory>
          </div>

          {/* Notes Card - Desktop Only */}
          <div className="hidden lg:block bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Notes <span className="text-sm font-normal text-gray-500">(Hidden)</span>
              </h3>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-sm text-brand hover:text-brand-hover font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            
            {editingNotes ? (
              <div>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                  rows="6"
                  placeholder="Add internal notes..."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover disabled:opacity-50"
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setInternalNotes(invoice.internalNotes || '');
                      setEditingNotes(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {internalNotes || 'No notes added yet.'}
              </p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Invoice Header Info */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Invoice #{invoice.invoiceNumber}</h2>
                <p className="text-gray-600">Due {formatInvoiceDate(invoice.dueDate)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Created: {formatTimestampDate(invoice.createdAt)}</p>
                <p className="text-3xl font-bold">{formatCurrency(invoice.totalAmount || 0)}</p>
                <p className="text-sm text-gray-600 mt-1">Amount Paid: {formatCurrency(invoice.amountPaid || 0)}</p>
              </div>
            </div>
            
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-3 flex-wrap">
              {!isPaid && !isVoided ? (
                <>
                  {hasBeenSent && isOverdue ? (
                    <>
                      <button
                        onClick={() => { setSendMode('reminder'); setShowSendModal(true); }}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 font-medium"
                      >
                        Send Reminder
                      </button>
                      <button
                        onClick={() => { setSendMode('invoice'); setShowSendModal(true); }}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                      >
                        Resend Invoice
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { setSendMode('invoice'); setShowSendModal(true); }}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium"
                    >
                      {hasBeenSent ? 'Resend Invoice' : 'Send Invoice'}
                    </button>
                  )}
                  {invoice.invoiceNumber && (
                    <Link
                      to={`/merchant/invoices/${invoice.invoiceNumber}/edit`}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Edit Invoice
                    </Link>
                  )}
                  {invoice.invoiceNumber && (
                    <button
                      onClick={() => navigate(`/merchant/invoices/${invoice.invoiceNumber}/payment`)}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Receive Payment
                    </button>
                  )}
                  <PermissionGate permission={PERMISSIONS.credit_memos.apply}>
                    {customerCredit?.totalAvailableCreditCents > 0 && invoice.balanceDue > 0 && (
                      <button
                        onClick={() => setShowApplyCreditModal(true)}
                        className="px-4 py-2 bg-green-50 border border-green-300 text-green-700 rounded-lg hover:bg-green-100 font-medium flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Apply Credit ({formatCurrency(customerCredit.totalAvailableCreditCents / 100)})
                      </button>
                    )}
                  </PermissionGate>
                </>
              ) : isPaid ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowReceiptModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Send Receipt
                  </button>
                  <PermissionGate permission={PERMISSIONS.payments.refund}>
                    {hasRefundableTransactions() && (
                      <button
                        onClick={() => handleRefundClick()}
                        disabled={processingRefund}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 hover:border-orange-200"
                        title="Refund transaction"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        {processingRefund ? 'Processing...' : 'Refund'}
                      </button>
                    )}
                  </PermissionGate>
                </div>
              ) : null}
              </div>
              {!isPaid && !isVoided && !hasBeenSent && displayUnsentInPortal === false && (
                <p className="max-w-xl text-xs leading-4 text-gray-400">
                  This invoice won’t appear in the customer portal’s invoice list until it’s sent.
                </p>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Line Items</h3>
                <button className="text-sm text-brand hover:text-brand-hover font-medium">
                  Customize Columns
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto bg-gray-50 px-6">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product/Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Details</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">QTY</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4">
                        <p className="font-medium">{item.itemName || item.description || 'Product Name'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{item.description || 'Information about this item'}</p>
                      </td>
                      <td className="px-6 py-4 text-center">{item.quantity}</td>
                      <td className="px-6 py-4 text-right">{formatCurrency(item.unitPrice || 0)}</td>
                      <td className="px-6 py-4 text-right font-medium">{formatCurrency(item.totalPrice || 0)}</td>
                      <td className="px-6 py-4 text-center">
                        {item.isTaxable && (
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Bottom Section with Payment Options and Totals */}
            <div className="p-6 bg-gray-50 border-t border-gray-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Payment Methods and Notes */}
                <div className="space-y-6">
                  {/* Customer Payment Methods */}
                  <div>
                    <h3 className="font-semibold mb-3">Customer Payment Methods</h3>
                    <p className="text-gray-700">
                      {invoice.paymentMethods?.map(opt => 
                        opt === 'credit_card' ? 'Credit Card' : 
                        opt === 'bank' ? 'Bank' : opt
                      ).join(' | ')}
                    </p>
                  </div>

                  {/* Notes to Customer */}
                  {invoice.customerNotes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes to Customer</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {invoice.customerNotes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Side - Totals */}
                <div className="lg:text-right">
                  <div className="inline-block text-left">
                    <div className="space-y-2">
                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(invoice.subtotal || 0)}</span>
                      </div>
                      
                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Taxable Subtotal:</span>
                        <span className="font-medium">{formatCurrency(invoice.taxableSubtotal || 0)}</span>
                      </div>
                      
                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Sales Tax:</span>
                        <span className="font-medium">{formatCurrency(invoice.taxAmount || 0)}</span>
                      </div>
                      
                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Shipping:</span>
                        <span className="font-medium">{formatCurrency(invoice.shippingCost || 0)}</span>
                      </div>
                      
                      {namedDiscountLines(invoice.appliedDiscounts, invoice.discountAmount).map((row, index) => (
                        <div key={`named-discount-${index}`} className="flex justify-between gap-16">
                          <span className="text-gray-600">{row.title}:</span>
                          <span className="font-medium text-red-600">-{formatCurrency(row.amount)}</span>
                        </div>
                      ))}
                      
                      <div className="pt-2 border-t">
                        <div className="flex justify-between gap-16 text-lg font-semibold">
                          <span>Total:</span>
                          <span>{formatCurrency(invoice.totalAmount || 0)}</span>
                        </div>
                      </div>
                      
                      <div className="pt-2 mt-2 border-t border-gray-300">
                        <div className="flex justify-between gap-16">
                          <span className="text-gray-600">Balance Due:</span>
                          <span className="font-medium">{formatCurrency(invoice.balanceDue || 0)}</span>
                        </div>
                        <div className="flex justify-between gap-16 mt-1">
                          <span className="text-gray-600">Balance Paid:</span>
                          <span className="font-medium">{formatCurrency(invoice.balancePaid || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice History Card - Mobile/Tablet Only */}
          <div className="lg:hidden bg-gray-50 rounded-xl p-6">
            <h3 className="font-semibold text-lg mb-4">Invoice History</h3>

            <ScrollableInvoiceHistory>
              <div className="space-y-3">
                {historyGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="bg-white rounded-lg p-3">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => group.events.length > 1 && toggleHistoryGroup(groupIndex)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                          {getHistoryIcon(group.action)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                          {formatHistoryAction(group.action)}
                          {(group.action === 'payment_recorded' || group.action === 'refund_issued' || group.action === 'payment_voided') && group.events.length === 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (group.events[0]?.transactionId) {
                                  navigate(`/merchant/transactions/${group.events[0].transactionId}`);
                                } else {
                                  console.warn('No transaction ID available for this transaction');
                                }
                              }}
                              className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                            >
                              View
                            </button>
                          )}
                          {group.action === 'credit_applied' && group.events.length === 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const creditMemoId = group.events[0]?.actionDetails?.credit_memo_id;
                                if (creditMemoId) {
                                  setSelectedCreditMemoId(creditMemoId);
                                  setShowCreditMemoDetailsModal(true);
                                } else {
                                  console.warn('No credit memo ID available for this event');
                                }
                              }}
                              className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                            >
                              View
                            </button>
                          )}
                          </p>
                          {group.action === 'credit_applied' && group.events.length === 1 && (
                            <p className="text-sm text-green-600">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.credit_amount || 0) / 100)} via Credit Memo
                              {group.events[0].actionDetails?.credit_memo_number && ` #${group.events[0].actionDetails.credit_memo_number}`}
                            </p>
                          )}
                          {group.action === 'payment_recorded' && group.events.length === 1 && (
                            <p className="text-sm text-gray-500">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.amount || 0) / 100)} via {
                                formatPaymentMethodLabel(
                                  group.events[0].actionDetails?.payment_method
                                    || group.events[0].actionDetails?.paymentMethod
                                )
                              }
                            </p>
                          )}
                          {group.action === 'refund_issued' && group.events.length === 1 && (
                            <p className="text-sm text-gray-500">
                              {formatRefundHistoryAmount(group.events[0].actionDetails)} back to {
                                (() => {
                                  const method = group.events[0].actionDetails?.payment_method || group.events[0].actionDetails?.paymentMethod;
                                  if (method === 'credit_card' || method === 'card') return 'Card';
                                  if (method === 'bank' || method === 'bank_transfer' || method === 'wire' || method === 'ach') return 'Bank';
                                  if (method === 'external' || method === 'other') return 'Other';
                                  if (method === 'check') return 'Check';
                                  if (method === 'cash') return 'Cash';
                                  return method || 'Card';
                                })()
                              }
                            </p>
                          )}
                          {group.action === 'payment_voided' && group.events.length === 1 && (
                            <p className="text-sm text-red-500">
                              {formatCurrency((group.events[0].actionDetails?.amount_cents || group.events[0].actionDetails?.void_amount || 0) / 100)} voided
                              {group.events[0].actionDetails?.reason && ` - ${group.events[0].actionDetails.reason}`}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(group.firstDate).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}, {new Date(group.firstDate).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </p>
                        </div>
                      </div>
                      {group.events.length > 1 && (
                        <svg 
                          className={`w-4 h-4 text-gray-400 transition-transform ${expandedHistoryGroups[groupIndex] ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                    
                    {expandedHistoryGroups[groupIndex] && group.events.length > 1 && (
                      <div className="mt-3 pl-11 border-l-2 border-gray-200 ml-5 space-y-4">
                        {group.events.map((event, eventIndex) => (
                          <div key={eventIndex} className="pl-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm text-gray-400">
                                {group.action === 'payment_recorded' ? 'Received' : 
                                 group.action === 'refund_issued' ? 'Refunded' : 
                                 formatHistoryAction(group.action)}
                                {event.transactionId && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/merchant/transactions/${event.transactionId}`);
                                    }}
                                    className="text-brand font-normal ml-1 hover:underline focus:outline-none"
                                  >
                                    View
                                  </button>
                                )}
                              </p>
                              {group.action === 'payment_recorded' && (
                                <p className="text-sm text-gray-500">
                                  {formatCurrency((event.actionDetails?.amount_cents || event.actionDetails?.amount || 0) / 100)} via {
                                    formatPaymentMethodLabel(
                                      event.actionDetails?.payment_method
                                        || event.actionDetails?.paymentMethod
                                    )
                                  }
                                </p>
                              )}
                              {group.action === 'refund_issued' && (
                                <p className="text-sm text-gray-500">
                                  {formatRefundHistoryAmount(event.actionDetails)} back to {
                                    (() => {
                                      const method = event.actionDetails?.payment_method || event.actionDetails?.paymentMethod;
                                      if (method === 'credit_card' || method === 'card') return 'Card';
                                      if (method === 'bank' || method === 'bank_transfer' || method === 'wire' || method === 'ach') return 'Bank';
                                      if (method === 'external' || method === 'other') return 'Other';
                                      if (method === 'check') return 'Check';
                                      if (method === 'cash') return 'Cash';
                                      return method || 'Card';
                                    })()
                                  }
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {new Date(event.actionDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}, {new Date(event.actionDate).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {(!invoice.history || invoice.history.length === 0) && (
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Invoice Created</p>
                        <p className="text-xs text-gray-500">
                          {new Date(invoice.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}, {new Date(invoice.createdAt).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollableInvoiceHistory>
          </div>

          {/* Notes Card - Mobile/Tablet Only */}
          <div className="lg:hidden bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Notes <span className="text-sm font-normal text-gray-500">(Hidden)</span>
              </h3>
              {!editingNotes && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-sm text-brand hover:text-brand-hover font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            
            {editingNotes ? (
              <div>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                  rows="6"
                  placeholder="Add internal notes..."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover disabled:opacity-50"
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setInternalNotes(invoice.internalNotes || '');
                      setEditingNotes(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {internalNotes || 'No notes added yet.'}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {refundError && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between items-center">
          <span>{refundError}</span>
          <button 
            onClick={() => setRefundError('')}
            className="text-red-700 hover:text-red-900"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundError('');
          setSelectedTransaction(null);
        }}
        onConfirm={handleRefundConfirm}
        transaction={selectedTransaction}
        processing={processingRefund}
        apiError={refundError}
      />

      {/* Apply Credit Modal */}
      <ApplyCreditModal
        isOpen={showApplyCreditModal}
        onClose={() => setShowApplyCreditModal(false)}
        invoice={{
          id: invoice?.id,
          invoiceNumber: invoice?.invoiceNumber,
          totalAmountCents: Math.round((invoice?.totalAmount || 0) * 100),
          amountPaidCents: Math.round((invoice?.amountPaid || 0) * 100)
        }}
        customerId={invoice?.customerId}
        onApplySuccess={async (result) => {
          setSuccessMessage(`Credit of ${formatCurrency(result.amountAppliedCents / 100)} applied successfully`);
          // Refresh invoice data
          if (invoiceNumber && invoiceNumber !== 'undefined') {
            try {
              const data = await invoiceService.getInvoice(invoiceNumber);
              const updatedInvoice = data.success ? data.invoice : null;
              if (updatedInvoice) {
                updatedInvoice.subtotal = parseFloat(updatedInvoice.subtotal) || 0;
                updatedInvoice.taxableSubtotal = parseFloat(updatedInvoice.taxableSubtotal) || 0;
                updatedInvoice.taxAmount = parseFloat(updatedInvoice.taxAmount) || 0;
                updatedInvoice.discountAmount = parseFloat(updatedInvoice.discountAmount) || 0;
                updatedInvoice.shippingCost = parseFloat(updatedInvoice.shippingCost) || 0;
                updatedInvoice.totalAmount = parseFloat(updatedInvoice.totalAmount) || 0;
                updatedInvoice.amountPaid = parseFloat(updatedInvoice.amountPaid) || 0;
                updatedInvoice.balanceDue = (updatedInvoice.totalAmount - updatedInvoice.amountPaid);
                updatedInvoice.balancePaid = updatedInvoice.amountPaid;
                updatedInvoice.items = (data.items || []).map(item => ({
                  ...item,
                  unitPrice: parseFloat(item.unitPrice) || 0,
                  total: parseFloat(item.total) || 0
                }));
                setInvoice(updatedInvoice);
              }
              // Refresh customer credit
              const creditResult = await creditMemoService.getCustomerCredit(`cust-${invoice.customerPublicId}`);
              if (creditResult.success) {
                setCustomerCredit(creditResult);
              }
              // Refresh history
              await refreshInvoiceHistory();
            } catch (err) {
              console.error('Error refreshing invoice:', err);
            }
          }
          setTimeout(() => setSuccessMessage(''), 5000);
        }}
      />

      {/* Credit Memo Details Modal - For viewing applied credits in history */}
      <CreditMemoDetailsModal
        isOpen={showCreditMemoDetailsModal}
        onClose={() => {
          setShowCreditMemoDetailsModal(false);
          setSelectedCreditMemoId(null);
        }}
        creditMemo={selectedCreditMemoId ? { id: selectedCreditMemoId } : null}
      />

      <SendInvoiceModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        invoice={invoice}
        onSuccess={handleSendInvoiceSuccess}
        mode={sendMode}
      />

      <SendReceiptModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        invoice={invoice}
        onSuccess={async (result) => {
          setSuccessMessage(result?.message || 'Receipt sent successfully');
          setTimeout(() => setSuccessMessage(''), 5000);
          await refreshInvoiceHistory();
        }}
      />

      <PdfPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        invoiceNumber={invoice?.invoiceNumber}
      />
    </div>
  );
}

// Helper function to format history actions
function formatHistoryAction(action) {
  const actionMap = {
    'create': 'Invoice Created',
    'invoice_created': 'Invoice Created',
    'update': 'Invoice Updated',
    'invoice_updated': 'Invoice Updated',
    'status_change': 'Status Changed',
    'status_changed': 'Status Changed',
    'payment_recorded': 'Payment Received',
    'payment_voided': 'Payment Voided',
    'refund_issued': 'Refund Issued',
    'credit_applied': 'Credit Applied',
    'sent': 'Invoice Sent',
    'invoice_sent': 'Invoice Sent',
    'receipt_sent': 'Receipt Sent',
    'reminder_sent': 'Reminder Sent',
    'viewed': 'Viewed by Customer',
    'invoice_viewed': 'Viewed by Customer',
    'delete': 'Invoice Deleted',
    'invoice_voided': 'Invoice Voided'
  };
  return actionMap[action] || action;
}

// Helper function to get history icon
function getHistoryIcon(action) {
  const iconMap = {
    'create': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    ),
    'invoice_created': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    ),
    'sent': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'invoice_sent': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'reminder_sent': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'viewed': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    'invoice_viewed': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    'payment_recorded': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'credit_applied': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'refund_issued': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
      </svg>
    ),
    'payment_voided': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    ),
    'update': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    'invoice_updated': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    'status_changed': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    'invoice_voided': (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    )
  };
  
  return iconMap[action] || iconMap['create'];
}

export default InvoiceDetails;